"use client";
import React from 'react';
import Image from "next/image";
import Link from 'next/link';
import BreadcrumbIcon from './BreadcrumbIcon';

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface HeroHeaderProps {
  imageSrc: string;
  imageAlt?: string;
  title?: string;
  subtitle?: string;
  highlight_title?: string;
  breadcrumbs?: BreadcrumbItem[];
  showBreadcrumbs?: boolean;
}

export const HeroHeader: React.FC<HeroHeaderProps> = ({
  imageSrc = '/bg-header.png',
  imageAlt = "Hero background",
  title = "",
  subtitle = "",
  breadcrumbs = [{ label: "", href: "" }],
  showBreadcrumbs = false,
}) => {
  return (
    <div className="relative w-full z-50  ">


      {/* Container com a máscara aplicada - desktop */}
      <div className="hidden lg:block relative w-full h-[448px] overflow-hidden z-50">
        {/* SVG com clipPath e gradiente */}
        <svg
          width="0"
          height="0"
          className="absolute"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <clipPath id="heroClipPath" clipPathUnits="objectBoundingBox">
              <path d="M-0.00885 -0.00719 L1.01146 -0.00719 V0.77803 C0.52318 1.38609 0.41094 0.51079 -0.00885 0.88634 L-0.00885 -0.00719 Z" />
            </clipPath>
            <linearGradient
              id="heroCurveGradient"
              x1="1922.5"
              y1="310.501"
              x2="283.5"
              y2="-464.498"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#EA5644" />
              <stop offset="0.34759" stopColor="#05293E" />
              <stop offset="0.603595" stopColor="#1E4441" />
              <stop offset="0.798077" stopColor="#598648" />
            </linearGradient>
          </defs>
        </svg>

        {/* Imagem de fundo com clipPath aplicado */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{ clipPath: 'url(#heroClipPath)' }}
        >
          <Image
            src={imageSrc}
            className="absolute inset-0 w-full h-full object-cover object-center"
            alt={imageAlt}
            width={1920}
            height={448}
            sizes="100vw"
            priority
          />

          {/* Gradiente overlay sobre a imagem */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(283.2deg, rgba(234, 86, 68, 0.7) 2.89%, rgba(5, 41, 62, 0.7) 31.71%, rgba(30, 68, 65, 0.7) 52.94%, rgba(89, 134, 72, 0.7) 69.06%)'
            }}
          >
            {/* Overlay adicional para escurecer um pouco */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20"></div>
          </div>
        </div>

        {/* Text overlay inside the banner */}
        <div className="absolute inset-0 z-20 container mx-auto px-4 sm:px-6 flex flex-col justify-center">
          <div className="pl-4 sm:pl-6 lg:pl-10">


            {/* Main Titles */}
            <div className="space-y-1 sm:space-y-2">
              {subtitle && (
                <p className="justify-start text-stone-50 text-xs sm:text-sm md:text-base font-normal font-['Poppins'] uppercase">
                  {subtitle}
                </p>
              )}
              <h1
                className="justify-start text-stone-50 text-xl sm:text-2xl md:text-3xl lg:text-[40px] font-normal font-['Rubik'] leading-tight"
                suppressHydrationWarning
              >
                {title}
              </h1>
            </div>

            {/* Breadcrumbs (top, smaller text) */}
            {showBreadcrumbs && breadcrumbs && breadcrumbs.length > 0 && (
              <nav aria-label="Breadcrumb" className="mt-2 sm:mt-3">
                <ol className="flex items-start gap-1 sm:gap-2 text-[#f9f7f7] text-[10px] sm:text-xs md:text-sm uppercase">
                  {breadcrumbs.map((item, index) => (
                    <React.Fragment key={index}>
                      <li>
                        <Link href={item.href} className="hover:text-[#f9f7f7]/80">
                          {item.label}
                        </Link>
                      </li>
                      {index < breadcrumbs.length - 1 && (
                        <li aria-hidden="true" className="mx-1 sm:mx-2 text-[#f9f7f7]/80"><BreadcrumbIcon /></li>
                      )}
                    </React.Fragment>
                  ))}
                </ol>
              </nav>
            )}
          </div>
        </div>


      </div>

      {/* Container mobile com clipPath */}
      <div className="block lg:hidden relative w-full h-[280px] sm:h-[320px] md:h-[350px] overflow-hidden z-50">
        {/* SVG com clipPath para mobile */}
        <svg
          width="0"
          height="0"
          className="absolute"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <clipPath id="heroClipPathMobile" clipPathUnits="objectBoundingBox">
              <path d="M-0.00885 -0.00719 L1.01146 -0.00719 V0.77803 C0.52318 1.38609 0.41094 0.51079 -0.00885 0.88634 L-0.00885 -0.00719 Z" />
            </clipPath>
          </defs>
        </svg>

        {/* Imagem de fundo com clipPath aplicado */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{ clipPath: 'url(#heroClipPathMobile)' }}
        >
          <Image
            src={imageSrc}
            className="absolute inset-0 w-full h-full object-cover object-center"
            alt={imageAlt}
            width={768}
            height={350}
            sizes="100vw"
            priority
          />

          {/* Gradiente overlay sobre a imagem */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(283.2deg, rgba(234, 86, 68, 0.7) 2.89%, rgba(5, 41, 62, 0.7) 31.71%, rgba(30, 68, 65, 0.7) 52.94%, rgba(89, 134, 72, 0.7) 69.06%)'
            }}
          >
            {/* Overlay adicional para escurecer um pouco */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20"></div>
          </div>
        </div>

        {/* Text overlay inside the banner */}
        <div className="absolute inset-0 z-20 container mx-auto px-3 sm:px-6 flex flex-col justify-center">
          <div className="pl-2 sm:pl-4 md:pl-6">


            {/* Conteúdo principal */}
            <div className="relative z-10 flex items-center h-full">
              <div className="container mx-auto px-2 sm:px-4 md:px-8">
                <div className="max-w-3xl sm:w-full md:max-w-3xl space-y-2 sm:space-y-3 text-white sm:pt-8">

                  <h1
                    className="justify-start text-stone-50 text-2xl sm:text-1xl md:text-4xl font-normal font-['Rubik'] leading-tight"
                    suppressHydrationWarning
                  >
                    {title}
                  </h1>
                  <p className="justify-start text-stone-50 text-xs sm:text-sm md:text-base font-normal font-['Poppins'] uppercase">
                    {subtitle}
                  </p>
                  {/* Breadcrumbs (top, smaller text) */}
                  {showBreadcrumbs && breadcrumbs && breadcrumbs.length > 0 && (
                    <nav aria-label="Breadcrumb" className="mt-1 sm:mt-2">
                      <ol className="flex items-start gap-1 text-[#f9f7f7] text-[9px] sm:text-[10px] md:text-xs uppercase">
                        {breadcrumbs.map((item, index) => (
                          <React.Fragment key={index}>
                            <li>
                              <Link href={item.href} className="hover:text-[#f9f7f7]/80">
                                {item.label}
                              </Link>
                            </li>
                            {index < breadcrumbs.length - 1 && (
                              <li aria-hidden="true" className="mx-1 text-[#f9f7f7]/80"><BreadcrumbIcon /></li>
                            )}
                          </React.Fragment>
                        ))}
                      </ol>
                    </nav>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>


      </div>

    </div>
  );
};

export default HeroHeader;
